import * as signalR from "@microsoft/signalr";

class AudioStreamPlayer {
    constructor(sampleRate = 8000) {
        // 1. 初始化浏览器音频上下文
        this.context = new (window.AudioContext || window.webkitAudioContext)({ sampleRate });
        this.nextStartTime = 0;
        // 存储麦克风相关资源
        this.micStream = null;
        this.micAudioContext = null;
        this.micSource = null;
        // 麦克风初始化状态
        this.isInitializing = false;
        this.isInitialized = false;
        // 停止标志，用于在异步初始化过程中检测是否需要停止
        this.stopRequested = false;
        this.sendAudioSubject = null;
        this.voiceNode = null;
    }

    // 解码函数：将 G.711u 字节转为浏览器需要的浮点数
    decodeMuLaw(uint8Array) {
        const float32Array = new Float32Array(uint8Array.length);
        for (let i = 0; i < uint8Array.length; i++) {
            let mu = ~uint8Array[i];
            let sign = (mu & 0x80) ? -1 : 1;
            let exponent = (mu >> 4) & 0x07;
            let mantissa = mu & 0x0F;
            let sample = sign * ((mantissa << (exponent + 3)) + (132 << exponent) - 132);
            float32Array[i] = sample / 32768;
        }
        return float32Array;
    }

    playChunk(uint8Data) {
        if (this.context.state === 'suspended') {
            this.context.resume(); // 解决浏览器必须用户交互才能播放的问题
        }

        const float32Data = this.decodeMuLaw(uint8Data);
        const audioBuffer = this.context.createBuffer(1, float32Data.length, this.context.sampleRate);
        audioBuffer.getChannelData(0).set(float32Data);

        const source = this.context.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.context.destination);

        // 自动平滑排队播放
        const currentTime = this.context.currentTime;
        if (this.nextStartTime < currentTime) {
            this.nextStartTime = currentTime + 0.02; // 20ms 的小缓冲
        }

        source.start(this.nextStartTime);
        this.nextStartTime += audioBuffer.duration;
    }

    async startMicCapture(signalrConnection) {
        // 如果正在初始化或已经初始化，直接返回
        if (this.isInitializing || this.isInitialized) return;

        // 先停止可能存在的旧捕获
        this.stopMicCapture();
        // 重置停止标志
        this.stopRequested = false;

        try {
            this.isInitializing = true;
            // 1. 获取麦克风流
            this.micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,      // 回声消除（极其重要，防止对方听到自己的回音）
                    noiseSuppression: true,      // 降噪（滤除背景底噪）
                    autoGainControl: true,       // 自动增益控制（防止说话声音太小）
                    channelCount: 1,             // 强制单声道，减少干扰
                    sampleRate: 48000            // 保持高采样率输入，在 Worklet 里再降
                }
            });
            // 检查是否收到停止请求
            if (this.stopRequested) {
                console.log("Stop requested during mic initialization, cleaning up...");
                this.stopMicCapture();
                return;
            }
            this.sendAudioSubject = new signalR.Subject();
            await signalrConnection.send("SendAudioStream", this.sendAudioSubject);
            this.micAudioContext = new (window.AudioContext || window.webkitAudioContext)();
            if (this.micAudioContext.state === 'suspended') {
                await this.micAudioContext.resume();
            }
            // 2. 加载 Worklet 模块
            await this.micAudioContext.audioWorklet.addModule('/voice-processor.js');
            this.micSource = this.micAudioContext.createMediaStreamSource(this.micStream);
            // 3. 创建 Worklet 节点
            this.voiceNode = new AudioWorkletNode(this.micAudioContext, 'voice-processor');
            // 4. 监听来自 Worklet 线程的数据
            this.voiceNode.port.onmessage = (event) => this.sendAudioSubject.next(event.data);
            this.micSource.connect(this.voiceNode);
            // 检查是否收到停止请求
            if (this.stopRequested) {
                console.log("Stop requested after mic initialization, cleaning up...");
                this.stopMicCapture();
                return;
            }
            this.isInitialized = true;
            this.isInitializing = false;
        } catch (error) {
            console.error("Error starting mic capture:", error);
            this.isInitializing = false;
            this.isInitialized = false;
            this.stopRequested = false;
            this.stopMicCapture();
            throw error;
        }
    }

    /**
     * 停止麦克风捕获并释放资源
     */
    stopMicCapture() {
        // 设置停止标志，用于在异步初始化过程中检测是否需要停止
        this.stopRequested = true;
        // 断开音频源连接
        if (this.micSource) {
            try {
                this.micSource.disconnect();
            } catch (e) {
                console.warn("Error disconnecting source:", e);
            }
            this.micSource = null;
        }

        // 关闭音频上下文
        if (this.micAudioContext) {
            try {
                if (this.micAudioContext.state !== 'closed') {
                    this.micAudioContext.close();
                }
            } catch (e) {
                console.warn("Error closing audio context:", e);
            }
            this.micAudioContext = null;
        }
        if (this.voiceNode) {
            this.voiceNode.port.onmessage = null;
            this.voiceNode.disconnect();
        }
        // 停止媒体流
        if (this.micStream) {
            try {
                this.micStream.getTracks().forEach(track => track.stop());
            } catch (e) {
                console.warn("Error stopping media stream:", e);
            }
            this.micStream = null;
        }
        if (this.sendAudioSubject) {
            // this.sendAudioSubject.complete();
            this.sendAudioSubject = null;
        }
        // 重置初始化状态
        this.isInitializing = false;
        this.isInitialized = false;
        console.log("Microphone capture stopped and resources released.");
    }
}
export default new AudioStreamPlayer();
